import { Fragment, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { InputMoney, InputPercentage } from '@/design_system/InputNumber';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';
import { useUpdateActionType, useUpdatePackActionType } from '@/models/actionType';
import { AgreementFromGetMany, useAgreements } from '@/models/agreements';
import { useUpdateSparePartType } from '@/models/sparePartType';
import useViewPort from '@/utils/useViewport';

import { hasAgreementPricesError } from '../Actions/utils/hasAgreementPricesError';

type Prices =
  | Endpoints['GET /action-types/organization']['response']['actionTypes'][number]['agreementPrices']
  | Endpoints['GET /action-types/workshop/:workshop-id']['response']['actionTypes'][number]['agreementPrices']
  | Endpoints['GET /spare-part-types/organization']['response'][number]['agreementPrices'];

export const AgreementPricesDialog = (props: {
  id: string;
  name: string;
  agreementPrices: Prices;
  mode: 'brand' | 'workshop';
  type: 'action' | 'pack-action' | 'spare-part';
}) => {
  const name = props.name;

  return (
    <Drawer
      trigger={
        <Button variant="secondary" iconOnly size="small">
          $
        </Button>
      }
    >
      <DrawerHeader>
        <Title size="300">
          <Trans id="settings.actions.prices-drawer.title">Prices for {name}</Trans>
        </Title>
      </DrawerHeader>
      <AgreementPricesDialogContent {...props} />
    </Drawer>
  );
};

const AgreementPricesDialogContent = ({
  id,
  name,
  agreementPrices,
  mode,
  type,
}: {
  id: string;
  name: string;
  agreementPrices: Prices;
  mode: 'brand' | 'workshop';
  type: 'action' | 'pack-action' | 'spare-part';
}) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { isMobile } = useViewPort();

  const initialClientAgreementPrices =
    (agreementPrices
      ?.filter((agreementPrice) => !!agreementPrice.agreement.clientB)
      .map((agreementPrice) => ({
        agreement: agreementPrice.agreement,
        amountBeforeTaxes: agreementPrice.amountBeforeTaxes,
        taxRate: agreementPrice.taxes[0]?.rate ?? 0,
        amount: agreementPrice.amount,
        margin: agreementPrice.margin,
        key: agreementPrice.id,
      })) as AgreementPrices) ?? [];

  const initialWorkshopAgreementPrices =
    (agreementPrices
      ?.filter((agreementPrice) => !agreementPrice.agreement.clientB)
      .map((agreementPrice) => ({
        agreement: agreementPrice.agreement,
        amountBeforeTaxes: agreementPrice.amountBeforeTaxes,
        taxRate: agreementPrice.taxes[0]?.rate ?? 0,
        amount: agreementPrice.amount,
        margin: agreementPrice.margin,
        key: agreementPrice.id,
      })) as AgreementPrices) ?? [];

  const [clientAgreementPrices, setClientAgreementPrices] = useState(initialClientAgreementPrices);
  const [workshopAgreementPrices, setWorkshopAgreementPrices] = useState(
    initialWorkshopAgreementPrices
  );

  const [isEditing, setIsEditing] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { data: { agreements = [] } = {} } = useAgreements(
    { limit: 500, types: ['price_catalog'] },
    { enabled: mode === 'brand' && isEditing }
  );

  const hasError =
    hasAgreementPricesError(clientAgreementPrices) ||
    hasAgreementPricesError(workshopAgreementPrices);

  const { mutateAsync: updateActionType, isPending: isUpdateActionTypePending } =
    useUpdateActionType();

  const { mutateAsync: updatePackActionType, isPending: isUpdatePackActionTypePending } =
    useUpdatePackActionType();

  const { mutateAsync: updateSparePartType, isPending: isUpdateSparePartTypePending } =
    useUpdateSparePartType();

  const onSave = async () => {
    setShowErrors(true);

    if (hasError) {
      return;
    }

    const body = {
      agreementPrices: [...clientAgreementPrices, ...workshopAgreementPrices].map(
        (agreementPrice) => ({
          agreementId: agreementPrice.agreement!.id,
          currency: agreementPrice.agreement!.currency ?? 'EUR',
          amountBeforeTaxes:
            agreementPrice.agreement?.amountToConsider === 'amount'
              ? undefined
              : agreementPrice.amountBeforeTaxes,
          taxRate: agreementPrice.taxRate ?? undefined,
          amount:
            agreementPrice.agreement?.amountToConsider === 'amount'
              ? agreementPrice.amount
              : undefined,
          margin: agreementPrice.agreement?.clientB ? agreementPrice.margin : undefined,
        })
      ),
    };

    try {
      if (type === 'pack-action') {
        await updatePackActionType({
          packActionTypeId: id,
          ...body,
        });
      } else if (type === 'action') {
        await updateActionType({
          actionTypeId: id,
          ...body,
        });
      } else if (type === 'spare-part') {
        await updateSparePartType({
          sparePartTypeId: id,
          ...body,
        });
      }

      showToast({
        text: (
          <Trans id="settings.actions.prices-drawer.success">
            &quot;{name}&quot; successfully saved
          </Trans>
        ),
      });

      setIsEditing(false);
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <DrawerBody>
        <Stack padding={isMobile ? '1rem' : '2rem'} gap="1rem">
          {mode === 'brand' && (
            <>
              <AgreementPricesInputs
                label={t({
                  id: 'settings.actions.prices-drawer.client-prices',
                  message: 'Client prices',
                })}
                agreementPrices={clientAgreementPrices}
                setAgreementPrices={setClientAgreementPrices}
                isDisabled={!isEditing}
                agreementOptions={agreements.filter((agreement) => !!agreement.clientB)}
                showErrors={showErrors}
                showMargin
              />
              <AgreementPricesInputs
                label={t({
                  id: 'settings.actions.prices-drawer.workshop-prices',
                  message: 'Workshop prices',
                })}
                agreementPrices={workshopAgreementPrices}
                setAgreementPrices={setWorkshopAgreementPrices}
                isDisabled={!isEditing}
                agreementOptions={agreements.filter((agreement) => !agreement.clientB)}
                showErrors={showErrors}
              />
            </>
          )}
          {mode === 'workshop' && (
            <AgreementPricesInputs
              label={t({
                id: 'settings.actions.prices-drawer.prices',
                message: 'Prices',
              })}
              agreementPrices={workshopAgreementPrices}
              setAgreementPrices={setWorkshopAgreementPrices}
              isDisabled
            />
          )}
        </Stack>
      </DrawerBody>
      {mode === 'brand' && (
        <Footer>
          {(apiError || (showErrors && hasError)) && (
            <FooterError>
              {!apiError && showErrors && hasError ? (
                <Trans id="settings.actions.prices-drawer.error">
                  Please fill the missing information
                </Trans>
              ) : (
                apiError
              )}
            </FooterError>
          )}
          <FooterButtons>
            {isEditing && (
              <>
                <Button
                  variant="secondary-danger"
                  size="medium"
                  onPress={() => {
                    setIsEditing(false);
                    setClientAgreementPrices(initialClientAgreementPrices);
                    setWorkshopAgreementPrices(initialWorkshopAgreementPrices);
                  }}
                >
                  <Trans id="settings.actions.prices-drawer.cancel">Cancel</Trans>
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  onPress={onSave}
                  isLoading={
                    isUpdateActionTypePending ||
                    isUpdatePackActionTypePending ||
                    isUpdateSparePartTypePending
                  }
                >
                  <Trans id="settings.actions.prices-drawer.save">Save</Trans>
                </Button>
              </>
            )}
            {!isEditing && (
              <Button variant="secondary" size="medium" onPress={() => setIsEditing(true)}>
                <Trans id="settings.actions.prices-drawer.edit">Edit prices</Trans>
              </Button>
            )}
          </FooterButtons>
        </Footer>
      )}
    </>
  );
};

type Agreement =
  Endpoints['GET /action-types/organization']['response']['actionTypes'][number]['agreementPrices'][number]['agreement'];

export type AgreementPrices = {
  agreement: Agreement | null;
  amountBeforeTaxes: number | null;
  taxRate: number | null;
  amount: number | null;
  margin: number | null;
  key: string;
}[];

const AgreementPricesInputs = ({
  agreementPrices,
  setAgreementPrices,
  label,
  isDisabled,
  agreementOptions = [],
  showErrors,
  showMargin,
}: {
  agreementPrices: AgreementPrices;
  setAgreementPrices: (agreementPrices: AgreementPrices) => void;
  label?: string;
  isDisabled: boolean;
  agreementOptions?: AgreementFromGetMany[];
  showErrors?: boolean;
  showMargin?: boolean;
}) => {
  const { t } = useLingui();
  const getAgreementLabel = useGetAgreementLabel();
  const { isTablet, isMobile } = useViewPort();

  return (
    <Stack gap="0.5rem">
      {!!label && (
        <InlineText size="50" weight="medium">
          {label}
        </InlineText>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isTablet || isMobile ? '1fr 1fr 1fr 1fr' : '3fr 1fr 1fr 1fr 1fr',
          columnGap: '0.25rem',
          rowGap: '0.5rem',
        }}
      >
        {!(isTablet || isMobile) && (
          <Paragraph size="200">
            {t({
              id: 'settings.actions.prices-drawer.agreement',
              message: 'Agreement',
            })}
          </Paragraph>
        )}
        <Paragraph size="200">
          {t({
            id: 'settings.actions.prices-drawer.amount-before-taxes',
            message: 'Amount w/o tax',
          })}
        </Paragraph>
        <Paragraph size="200">
          {t({
            id: 'settings.actions.prices-drawer.tax-rate',
            message: 'Tax rate',
          })}
        </Paragraph>
        <Paragraph size="200">
          {t({
            id: 'settings.actions.prices-drawer.amount',
            message: 'Amount',
          })}
        </Paragraph>
        {showMargin ? (
          <Paragraph size="200">
            {t({
              id: 'settings.actions.prices-drawer.margin',
              message: 'Margin',
            })}
          </Paragraph>
        ) : (
          <div />
        )}
        {agreementPrices.map((agreementPrice) => {
          return (
            <Fragment key={agreementPrice.key}>
              <Stack
                row
                gap="0.5rem"
                alignItems="center"
                style={{ flex: 1, gridColumn: isTablet || isMobile ? '1 / -1' : undefined }}
              >
                {!isDisabled && (
                  <Button
                    ariaLabel={t({
                      id: 'settings.actions.prices-drawer.delete',
                      message: `Remove`,
                    })}
                    tooltip={t({
                      id: 'settings.actions.prices-drawer.delete',
                      message: `Remove`,
                    })}
                    iconOnly
                    size="large"
                    variant="secondary-danger"
                    onPress={() =>
                      setAgreementPrices(
                        agreementPrices.filter((p) => p.key !== agreementPrice.key)
                      )
                    }
                    disabled={isDisabled}
                  >
                    <IconTrash />
                  </Button>
                )}
                <InputSelect
                  isSearchable={false}
                  options={agreementOptions.filter(
                    ({ id }) =>
                      agreementPrice.agreement?.id === id ||
                      !agreementPrices.some(({ agreement }) => agreement?.id === id)
                  )}
                  value={agreementPrice.agreement}
                  aria-label={t({
                    id: 'settings.actions.prices-drawer.agreement',
                    message: 'Agreement',
                  })}
                  placeholder={t({
                    id: 'settings.actions.prices-drawer.agreement',
                    message: 'Agreement',
                  })}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => getAgreementLabel(option)}
                  formatOptionLabel={(option) => <AgreementCard agreement={option} />}
                  onChange={(option) =>
                    setAgreementPrices(
                      agreementPrices.map((p) =>
                        agreementPrice.key === p.key
                          ? {
                              ...agreementPrice,
                              agreement: option ?? null,
                              currency: option ? option.currency : null,
                              amount: null,
                              amountBeforeTaxes: null,
                              taxRate: null,
                            }
                          : p
                      )
                    )
                  }
                  isInvalid={showErrors && !agreementPrice.agreement}
                  isDisabled={isDisabled}
                  style={{ flex: 1 }}
                />
              </Stack>
              <InputMoney
                ariaLabel={t({
                  id: 'settings.actions.prices-drawer.amount-before-taxes',
                  message: 'Amount w/o tax',
                })}
                placeholder={
                  isDisabled
                    ? '-'
                    : t({
                        id: 'settings.actions.prices-drawer.amount-before-taxes',
                        message: 'Amount w/o tax',
                      })
                }
                value={agreementPrice.amountBeforeTaxes ?? NaN}
                onChange={(value) =>
                  setAgreementPrices(
                    agreementPrices.map((p) =>
                      agreementPrice.key === p.key
                        ? {
                            ...agreementPrice,
                            amountBeforeTaxes: Number.isNaN(value) ? null : value,
                            amount: Number.isNaN(value)
                              ? null
                              : Math.round((value * (100 + (agreementPrice.taxRate ?? 0))) / 100),
                          }
                        : p
                    )
                  )
                }
                currency={agreementPrice.agreement?.currency ?? 'EUR'}
                isDisabled={
                  !agreementPrice.agreement ||
                  agreementPrice.agreement.amountToConsider === 'amount' ||
                  isDisabled
                }
                style={{ flex: 2 }}
              />
              <InputPercentage
                ariaLabel={t({
                  id: 'settings.actions.prices-drawer.tax-rate',
                  message: 'Tax rate',
                })}
                placeholder={
                  isDisabled
                    ? '-'
                    : t({
                        id: 'settings.actions.prices-drawer.tax-rate',
                        message: 'Tax rate',
                      })
                }
                value={agreementPrice.taxRate ?? NaN}
                onChange={(value) => {
                  const taxRate = Number.isNaN(value) ? 0 : value;

                  setAgreementPrices(
                    agreementPrices.map((p) =>
                      agreementPrice.key === p.key
                        ? {
                            ...agreementPrice,
                            taxRate,
                            ...(agreementPrice.agreement?.amountToConsider === 'amount'
                              ? {
                                  amountBeforeTaxes:
                                    agreementPrice.amount === null
                                      ? null
                                      : Math.round(
                                          (agreementPrice.amount * 100) / (100 + (taxRate ?? 0))
                                        ),
                                }
                              : {
                                  amount:
                                    agreementPrice.amountBeforeTaxes === null
                                      ? null
                                      : Math.round(
                                          (agreementPrice.amountBeforeTaxes * (100 + taxRate)) / 100
                                        ),
                                }),
                          }
                        : p
                    )
                  );
                }}
                minValue={0}
                maxValue={1}
                style={{ flex: 1 }}
                isDisabled={!agreementPrice.agreement || isDisabled}
              />
              <InputMoney
                ariaLabel={t({
                  id: 'settings.actions.prices-drawer.amount',
                  message: 'Amount',
                })}
                placeholder={
                  isDisabled
                    ? '-'
                    : t({
                        id: 'settings.actions.prices-drawer.amount',
                        message: 'Amount',
                      })
                }
                value={agreementPrice.amount ?? NaN}
                onChange={(value) =>
                  setAgreementPrices(
                    agreementPrices.map((p) =>
                      agreementPrice.key === p.key
                        ? {
                            ...agreementPrice,
                            amount: Number.isNaN(value) ? null : value,
                            amountBeforeTaxes: Number.isNaN(value)
                              ? null
                              : Math.round((value * 100) / (100 + (agreementPrice.taxRate ?? 0))),
                          }
                        : p
                    )
                  )
                }
                currency={agreementPrice.agreement?.currency ?? 'EUR'}
                isDisabled={
                  !agreementPrice.agreement ||
                  agreementPrice.agreement.amountToConsider === 'amountBeforeTaxes' ||
                  isDisabled
                }
                style={{ flex: 2 }}
              />
              {showMargin ? (
                <InputPercentage
                  ariaLabel={t({
                    id: 'settings.actions.prices-drawer.margin',
                    message: 'Margin',
                  })}
                  placeholder={
                    isDisabled
                      ? '-'
                      : t({
                          id: 'settings.actions.prices-drawer.margin',
                          message: 'Margin',
                        })
                  }
                  value={agreementPrice.margin ?? agreementPrice.agreement?.margin ?? NaN}
                  onChange={(value) => {
                    const margin = Number.isNaN(value) ? 0 : value;

                    setAgreementPrices(
                      agreementPrices.map((p) =>
                        agreementPrice.key === p.key
                          ? {
                              ...agreementPrice,
                              margin,
                            }
                          : p
                      )
                    );
                  }}
                  minValue={0}
                  maxValue={1}
                  style={{ flex: 1 }}
                  isDisabled={!agreementPrice.agreement || isDisabled}
                />
              ) : (
                <div />
              )}
            </Fragment>
          );
        })}
      </div>
      {agreementPrices.length < agreementOptions.length && !isDisabled && (
        <div>
          <Button
            variant="link-subtle"
            size="small"
            onPress={() => {
              setAgreementPrices([
                ...agreementPrices,
                {
                  key: crypto.randomUUID(),
                  agreement: null,
                  amountBeforeTaxes: null,
                  taxRate: null,
                  amount: null,
                  margin: null,
                },
              ]);
            }}
            disabled={!!agreementPrices.length && !agreementPrices.at(-1)?.agreement}
          >
            <IconAdd />
            <Trans id="settings.actions.prices-drawer.add">Add a price</Trans>
          </Button>
        </div>
      )}
    </Stack>
  );
};

const useGetAgreementLabel = () => {
  const { t } = useLingui();

  return (agreement: Agreement) => {
    const fromLabel = t({ id: 'settings.agreement.from', message: 'From' });
    const toLabel = t({ id: 'settings.agreement.to', message: 'To' });
    const clients = t({ id: 'settings.agreement.clients', message: 'Clients' });

    const from = agreement.clientA
      ? clients
      : [...agreement.entitiesA, ...agreement.entityGroupsA].map(({ name }) => name).join(', ');

    const to = agreement.clientB
      ? clients
      : [...agreement.entitiesB, ...agreement.entityGroupsB].map(({ name }) => name).join(', ');

    return `${fromLabel} ${from}; ${toLabel} ${to}`;
  };
};

const AgreementCard = ({ agreement }: { agreement: Agreement }) => {
  return (
    <Paragraph size="200">
      <p>
        <InlineText size="200" weight="medium">
          <Trans id="settings.agreement.from">From</Trans>
        </InlineText>{' '}
        <InlineText size="200" color="secondary">
          {agreement.clientA ? (
            <Trans id="settings.agreement.clients">Clients</Trans>
          ) : (
            [...agreement.entitiesA, ...agreement.entityGroupsA].map(({ name }) => name).join(', ')
          )}
        </InlineText>
      </p>
      <p>
        <InlineText size="200" weight="medium">
          <Trans id="settings.agreement.to">To</Trans>
        </InlineText>{' '}
        <InlineText size="200" color="secondary">
          {agreement.clientB ? (
            <Trans id="settings.agreement.clients">Clients</Trans>
          ) : (
            [...agreement.entitiesB, ...agreement.entityGroupsB].map(({ name }) => name).join(', ')
          )}
        </InlineText>
      </p>
      {agreement.articleBrands.length > 0 && (
        <p>
          <InlineText size="200" weight="medium">
            <Trans id="settings.agreement.brand">Brand</Trans>
          </InlineText>{' '}
          <InlineText size="200" color="secondary">
            {agreement.articleBrands.map(({ name }) => name).join(', ')}
          </InlineText>
        </p>
      )}
      {agreement.articleWorkshops.length > 0 && agreement.clientB && (
        <p>
          <InlineText size="200" weight="medium">
            <Trans id="settings.agreement.workshop">Workshop</Trans>
          </InlineText>{' '}
          <InlineText size="200" color="secondary">
            {agreement.articleWorkshops.map(({ name }) => name).join(', ')}
          </InlineText>
        </p>
      )}
    </Paragraph>
  );
};
